import React, { useMemo, useState, useEffect } from "react";
import { BRAND, PRODUCT, COLORS, COLOR_CATEGORIES, LENGTHS, FAQS, TESTIMONIALS, SHIPPING_OPTIONS } from "@/data/product";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { trackWhatsAppClick, trackEvent } from "@/lib/analytics";
import { useCart } from "@/context/CartContext";
import NewsletterForm from "@/components/NewsletterForm";
import WelcomePopup from "@/components/WelcomePopup";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Star, Truck, Shield, Sparkles, Flame, Droplets, Heart, ChevronDown, Instagram, Mail,
  ArrowRight, Check, MapPin, Globe, Search, ShoppingBag,
} from "lucide-react";

const IMG_HERO = "/images/hero.jpeg";
const IMG_TEXTURE = "/images/texture.jpeg";
const IMG_BUNDLES = "/images/bundles.jpg";
const IMG_DETAIL = "/images/detail.jpg";
const IMG_PALETTE = "/images/palette.jpeg";
const IMG_TESTI = IMG_PALETTE;

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
}

function shippingLine(s) {
  return s.price === 0 ? "Gratuit" : formatEUR(s.price);
}

function buildWhatsAppLink({ color, length, quantity, shipping }) {
  const lines = [
    `Bonjour ${BRAND.name} !`,
    ``,
    `Je souhaite commander :`,
    `• Produit : ${PRODUCT.name}`,
    `• Couleur : ${color.name} (${color.code})`,
    `• Longueur : ${length.label} — ${length.subtitle}`,
    `• Quantité : ${quantity} pièce${quantity > 1 ? "s" : ""}`,
    `• Livraison : ${shipping.label} — ${shippingLine(shipping)}`,
    ``,
    `Total estimé : ${formatEUR(PRODUCT.price * quantity + shipping.price)} (livraison ${shippingLine(shipping)} incluse)`,
    ``,
    `Merci de me confirmer la disponibilité et le mode de paiement.`,
  ];
  const message = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${BRAND.whatsappNumber}?text=${message}`;
}

const TrustItem = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 text-[#6B5D54]">
    <Icon className="h-4 w-4 text-[#C58C85]" strokeWidth={1.5} />
    <span className="text-xs md:text-sm font-medium tracking-wide">{label}</span>
  </div>
);

const StarRow = ({ n = 5 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${n} étoiles`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} className="h-3.5 w-3.5 fill-[#C58C85] text-[#C58C85]" />
    ))}
  </div>
);

const shippingIcons = {
  fr: Truck,
  eu: Globe,
  clickcollect: MapPin,
};

export default function Landing() {
  const [colorId, setColorId] = useState(COLORS[0].id);
  const [lengthId] = useState(LENGTHS[0].id);
  const [qty, setQty] = useState(2);
  const [shippingId, setShippingId] = useState(SHIPPING_OPTIONS[0].id);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const [stock, setStock] = useState({}); // colorId -> in_stock (default true)
  const cart = useCart();

  const color = useMemo(() => COLORS.find((c) => c.id === colorId), [colorId]);
  const length = useMemo(() => LENGTHS.find((l) => l.id === lengthId), [lengthId]);
  const shipping = useMemo(() => SHIPPING_OPTIONS.find((s) => s.id === shippingId), [shippingId]);

  const filteredColors = useMemo(() => {
    return COLORS.filter((c) => {
      const catOk = activeCat === "all" || c.category === activeCat;
      const q = search.trim().toLowerCase();
      const searchOk = !q || c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q);
      return catOk && searchOk;
    });
  }, [activeCat, search]);

  const subtotal = PRODUCT.price * qty;
  const total = subtotal + shipping.price;
  const waLink = buildWhatsAppLink({ color, length, quantity: qty, shipping });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Fetch stock map (out-of-stock colors)
  useEffect(() => {
    api.get("/colors/stock").then(({ data }) => setStock(data || {})).catch(() => {});
  }, []);

  // Pixel: ViewContent on selected color change
  useEffect(() => {
    trackEvent("ViewContent", {
      content_ids: [color.id],
      content_name: color.name,
      content_type: "product",
      value: PRODUCT.price,
      currency: "EUR",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colorId]);

  const isInStock = (cid) => stock[cid] !== false; // default true
  const currentInStock = isInStock(colorId);

  const handleAddToCart = () => {
    if (!currentInStock) {
      toast.error("Cette couleur est en rupture de stock.");
      return;
    }
    cart.addItem(color, qty);
    trackEvent("AddToCart", {
      content_ids: [color.id],
      content_name: color.name,
      content_type: "product",
      value: PRODUCT.price * qty,
      currency: "EUR",
      contents: [{ id: color.id, quantity: qty }],
    });
    toast.success("Ajouté au panier", { description: `${qty} × ${color.name}` });
  };

  const handleWhatsApp = () => {
    trackWhatsAppClick({
      color: color.name,
      colorCode: color.code,
      length: length.label,
      quantity: qty,
      shipping: shipping.label,
      subtotal,
      shippingFee: shipping.price,
      total,
      currency: "EUR",
    });
    toast.success("Ouverture de WhatsApp", {
      description: `${color.name} • ${length.label} • ${qty} pièce${qty > 1 ? "s" : ""} • ${shipping.label}`,
    });
    setTimeout(() => window.open(waLink, "_blank", "noopener,noreferrer"), 120);
  };

  return (
    <div className="min-h-screen bg-[#FDFBF9] text-[#2C2420] font-sans">
      {/* ============ NAV ============ */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled ? "backdrop-blur-xl bg-white/85 border-b border-[#EADBD1]" : "bg-transparent"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <a href="#top" className="flex items-baseline gap-2 group" data-testid="nav-logo">
            <span className="font-serif text-2xl md:text-3xl tracking-tight text-[#2C2420]">French Curl</span>
            <span className="font-sans text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#C58C85]">Vibes</span>
          </a>
          <nav className="hidden md:flex items-center gap-9 text-sm text-[#2C2420]">
            <a href="#produit" className="hover:text-[#C58C85] transition-colors">Le produit</a>
            <a href="#couleurs" className="hover:text-[#C58C85] transition-colors">Couleurs</a>
            <a href="#livraison" className="hover:text-[#C58C85] transition-colors">Livraison</a>
            <a href="#avis" className="hover:text-[#C58C85] transition-colors">Avis</a>
            <a href="#faq" className="hover:text-[#C58C85] transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => cart.open()}
              aria-label="Ouvrir le panier"
              data-testid="nav-cart-btn"
              className="relative h-10 w-10 rounded-full border border-[#EADBD1] bg-white hover:border-[#C58C85] grid place-items-center transition-colors"
            >
              <ShoppingBag className="h-4 w-4 text-[#2C2420]" />
              {cart.totalQuantity > 0 && (
                <span className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 rounded-full bg-[#2C2420] text-white text-[10px] font-medium grid place-items-center" data-testid="cart-count-badge">
                  {cart.totalQuantity}
                </span>
              )}
            </button>
            <Button
              onClick={handleWhatsApp}
              className="rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white px-5 h-10 shadow-sm hidden sm:inline-flex"
              data-testid="nav-whatsapp-btn"
            >
              <WhatsAppIcon className="h-4 w-4 mr-2" />
              WhatsApp
            </Button>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section id="top" className="relative pt-28 md:pt-36 pb-16 md:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <p className="text-xs md:text-sm uppercase tracking-[0.28em] font-medium text-[#C58C85] mb-6">
              60+ teintes · Édition limitée
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-[#2C2420]">
              French Curl.
              <br />
              <span className="italic text-[#C58C85]">Posée minute,</span>
              <br />
              tenue éternelle.
            </h1>
            <p className="mt-6 md:mt-8 text-base md:text-lg leading-relaxed text-[#6B5D54] max-w-xl">
              {PRODUCT.description}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <TrustItem icon={Truck} label="Livraison dès 5,90€ — Click & Collect gratuit" />
              <TrustItem icon={Shield} label="Commande via WhatsApp" />
              <TrustItem icon={Sparkles} label="Fibre premium" />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleWhatsApp}
                className="group rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white px-7 h-14 text-base shadow-lg shadow-[#25D366]/25 animate-soft-pulse"
                data-testid="hero-whatsapp-btn"
              >
                <WhatsAppIcon className="h-5 w-5 mr-2.5" />
                Commander sur WhatsApp
                <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-1" />
              </Button>
              <a
                href="#produit"
                className="inline-flex items-center justify-center rounded-full border border-[#EADBD1] hover:border-[#2C2420] text-[#2C2420] px-7 h-14 text-base transition-colors"
                data-testid="hero-discover-btn"
              >
                Voir les 60 teintes
                <ChevronDown className="h-4 w-4 ml-2" />
              </a>
            </div>

            <p className="mt-5 text-xs text-[#6B5D54]">✦ Plus de 12 000 clientes satisfaites — note moyenne 4,9/5</p>
          </div>

          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] w-full rounded-[2rem] overflow-hidden bg-[#F5EFE6] shadow-2xl shadow-[#2C2420]/10">
              <img
                src={IMG_HERO}
                alt="Modèle portant des braids French Curl"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C2420]/30 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 md:left-6 md:right-auto md:w-72 bg-white/95 backdrop-blur-md rounded-2xl p-4 md:p-5 shadow-xl border border-white/40">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#C58C85] font-medium">À partir de</p>
                    <p className="font-serif text-3xl text-[#2C2420] leading-none mt-1">{formatEUR(PRODUCT.price)}</p>
                    <p className="text-xs text-[#6B5D54] mt-1">la pièce · 60+ teintes</p>
                  </div>
                  <div className="flex flex-col items-end">
                    <StarRow />
                    <span className="text-[11px] text-[#6B5D54] mt-1">4,9 / 5</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="hidden md:flex absolute -top-4 -right-4 bg-[#2C2420] text-[#FDFBF9] rounded-full pl-2 pr-4 py-2 items-center gap-2 shadow-xl">
              <span className="bg-[#C58C85] rounded-full h-7 w-7 flex items-center justify-center">
                <Flame className="h-4 w-4" />
              </span>
              <span className="text-xs uppercase tracking-widest">Best-seller</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section className="border-y border-[#EADBD1] bg-[#F5EFE6]/60 py-6 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-wrap items-center justify-center md:justify-between gap-6 text-xs uppercase tracking-[0.25em] text-[#6B5D54]">
          <span>✦ 60+ couleurs disponibles</span>
          <span>✦ Longueur 24 pouces</span>
          <span>✦ Click & Collect gratuit</span>
          <span>✦ Livraison France 5,90€</span>
          <span>✦ WhatsApp 7/7</span>
        </div>
      </section>

      {/* ============ PRODUIT / SELECTOR ============ */}
      <section id="produit" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Gallery */}
          <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
            <div className="grid grid-cols-5 gap-3">
              <div className="col-span-5 aspect-square rounded-3xl overflow-hidden bg-[#F5EFE6]">
                <img src={IMG_TEXTURE} alt="Texture French Curl" className="w-full h-full object-cover" data-testid="product-image" />
              </div>
              <div className="col-span-3 aspect-[4/3] rounded-2xl overflow-hidden bg-[#F5EFE6]">
                <img src={IMG_BUNDLES} alt="Paquets de braids French Curl" className="w-full h-full object-cover" />
              </div>
              <div className="col-span-2 aspect-[4/3] rounded-2xl overflow-hidden bg-[#F5EFE6]">
                <img src={IMG_DETAIL} alt="Détail tresses French Curl" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          {/* Selector */}
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Configurez votre commande</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">{PRODUCT.name}</h2>
            <div className="flex items-baseline gap-3 mt-4">
              <span className="font-serif text-3xl text-[#2C2420]">{formatEUR(PRODUCT.price)}</span>
              <span className="text-sm text-[#6B5D54]">/ pièce</span>
              <span className="text-sm text-[#6B5D54]">· {COLORS.length} teintes</span>
            </div>

            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {PRODUCT.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm text-[#2C2420]">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-[#F5EFE6] grid place-items-center flex-shrink-0">
                    <Check className="h-3 w-3 text-[#C58C85]" strokeWidth={2.5} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <Separator className="my-8 bg-[#EADBD1]" />

            {/* Couleurs */}
            <div id="couleurs">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="font-sans text-base md:text-lg font-medium text-[#2C2420]">
                  Couleur — <span className="text-[#C58C85]">{color.name}</span>
                </h3>
                <span className="text-xs text-[#6B5D54]">Code <span className="font-mono">{color.code}</span></span>
              </div>

              {/* Category tabs */}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => setActiveCat("all")}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    activeCat === "all" ? "bg-[#2C2420] text-white border-[#2C2420]" : "border-[#EADBD1] text-[#2C2420] hover:border-[#C58C85]"
                  }`}
                  data-testid="cat-all"
                >
                  Toutes ({COLORS.length})
                </button>
                {COLOR_CATEGORIES.map((cat) => {
                  const count = COLORS.filter((c) => c.category === cat).length;
                  const active = activeCat === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveCat(cat)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        active ? "bg-[#2C2420] text-white border-[#2C2420]" : "border-[#EADBD1] text-[#2C2420] hover:border-[#C58C85]"
                      }`}
                      data-testid={`cat-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {cat} ({count})
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="mt-3 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B5D54]" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher une couleur (ex: 1B, miel, rose…)"
                  className="w-full h-10 pl-10 pr-4 rounded-full bg-white border border-[#EADBD1] focus:border-[#C58C85] focus:outline-none text-sm placeholder:text-[#6B5D54]/60"
                  data-testid="color-search"
                />
              </div>

              {/* Swatches grid */}
              <div className="mt-5 grid grid-cols-6 sm:grid-cols-8 md:grid-cols-10 gap-2.5 max-h-[340px] overflow-y-auto pr-1 -mr-1">
                {filteredColors.map((c) => {
                  const active = c.id === colorId;
                  const available = isInStock(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setColorId(c.id)}
                      title={`${c.name} (${c.code})${available ? "" : " — Rupture"}`}
                      aria-label={`Sélectionner ${c.name}`}
                      data-testid={`color-${c.id}`}
                      className={`group relative aspect-square rounded-xl transition-all duration-200 border ${
                        active
                          ? "ring-2 ring-offset-2 ring-[#2C2420] ring-offset-[#FDFBF9] border-transparent scale-105"
                          : "border-[#EADBD1] hover:scale-105 hover:border-[#C58C85]"
                      } ${!available ? "opacity-40" : ""}`}
                      style={{ background: c.swatch }}
                    >
                      {active && (
                        <span className="absolute inset-0 grid place-items-center">
                          <Check className="h-4 w-4 text-white drop-shadow-md" strokeWidth={3} />
                        </span>
                      )}
                      {!available && !active && (
                        <span className="absolute inset-0 grid place-items-center bg-black/20 rounded-xl">
                          <span className="text-[8px] uppercase tracking-wider text-white font-medium">Rupture</span>
                        </span>
                      )}
                      <span className="absolute inset-x-0 -bottom-5 text-[9px] text-[#6B5D54] text-center font-mono opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        {c.code}
                      </span>
                    </button>
                  );
                })}
                {filteredColors.length === 0 && (
                  <p className="col-span-full text-sm text-[#6B5D54] py-6 text-center">Aucune couleur ne correspond.</p>
                )}
              </div>
            </div>

            <Separator className="my-8 bg-[#EADBD1]" />

            {/* Longueur — unique */}
            <div>
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-sans text-base md:text-lg font-medium text-[#2C2420]">
                  Longueur — <span className="text-[#C58C85]">{length.label}</span>
                </h3>
                <span className="text-xs text-[#6B5D54]">Taille unique</span>
              </div>
              <div className="mt-4 rounded-2xl border border-[#2C2420] bg-[#2C2420] text-white p-5 flex items-center gap-5" data-testid="length-info">
                <p className="font-serif text-4xl leading-none">{length.label}</p>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-white/70">{length.subtitle}</p>
                  <p className="text-sm text-white/80 mt-1">Une seule longueur disponible — la plus polyvalente.</p>
                </div>
              </div>
            </div>

            <Separator className="my-8 bg-[#EADBD1]" />

            {/* Quantité */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-sans text-base md:text-lg font-medium text-[#2C2420]">Quantité</h3>
                <p className="text-sm text-[#6B5D54] mt-1">Recommandé : 2 à 3 pièces pour une pose complète</p>
              </div>
              <div className="flex items-center gap-3 rounded-full border border-[#EADBD1] bg-white px-2 py-1">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-9 w-9 rounded-full hover:bg-[#F5EFE6] text-[#2C2420] text-lg" data-testid="qty-decrease">−</button>
                <span className="w-6 text-center font-medium" data-testid="qty-display">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="h-9 w-9 rounded-full hover:bg-[#F5EFE6] text-[#2C2420] text-lg" data-testid="qty-increase">+</button>
              </div>
            </div>

            <Separator className="my-8 bg-[#EADBD1]" />

            {/* Livraison */}
            <div id="livraison">
              <h3 className="font-sans text-base md:text-lg font-medium text-[#2C2420]">
                Mode de livraison — <span className="text-[#C58C85]">{shipping.label}</span>
              </h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SHIPPING_OPTIONS.map((s) => {
                  const Icon = shippingIcons[s.id] || Truck;
                  const active = s.id === shippingId;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setShippingId(s.id)}
                      data-testid={`shipping-${s.id}`}
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        active
                          ? "border-[#2C2420] bg-white shadow-lg shadow-[#2C2420]/5"
                          : "border-[#EADBD1] bg-white hover:border-[#C58C85]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`h-9 w-9 rounded-full grid place-items-center flex-shrink-0 ${active ? "bg-[#2C2420] text-white" : "bg-[#F5EFE6] text-[#2C2420]"}`}>
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <p className="font-medium text-sm text-[#2C2420] truncate">{s.label}</p>
                            <p className={`text-sm font-medium ${s.price === 0 ? "text-[#25D366]" : "text-[#2C2420]"}`}>
                              {shippingLine(s)}
                            </p>
                          </div>
                          <p className="text-[11px] text-[#6B5D54] mt-0.5">{s.eta}</p>
                          <p className="text-xs text-[#6B5D54] mt-1.5 leading-snug">{s.description}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <Separator className="my-8 bg-[#EADBD1]" />

            {/* Total + CTA */}
            <div className="rounded-3xl bg-[#F5EFE6] p-6 md:p-7">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm text-[#6B5D54]">Sous-total ({qty} × {formatEUR(PRODUCT.price)})</span>
                <span className="text-sm text-[#2C2420]">{formatEUR(subtotal)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-[#6B5D54]">{shipping.label}</span>
                <span className={`text-sm ${shipping.price === 0 ? "text-[#25D366] font-medium" : "text-[#2C2420]"}`}>
                  {shippingLine(shipping)}
                </span>
              </div>
              <Separator className="my-4 bg-[#EADBD1]" />
              <div className="flex items-baseline justify-between mb-5">
                <span className="font-medium text-[#2C2420]">Total</span>
                <span className="font-serif text-3xl text-[#2C2420]" data-testid="total-price">{formatEUR(total)}</span>
              </div>

              {!currentInStock && (
                <div className="mb-3 rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-xs text-red-700" data-testid="out-of-stock-banner">
                  ⚠ Cette couleur est temporairement en rupture. Sélectionnez une autre teinte.
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Button
                  onClick={handleAddToCart}
                  disabled={!currentInStock}
                  className="rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white h-14 text-base shadow-lg shadow-[#2C2420]/15 disabled:opacity-50"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingBag className="h-5 w-5 mr-2" />
                  Ajouter au panier
                </Button>
                <Button
                  onClick={handleWhatsApp}
                  className="rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white h-14 text-base shadow-lg shadow-[#25D366]/25"
                  data-testid="selector-whatsapp-btn"
                >
                  <WhatsAppIcon className="h-5 w-5 mr-2.5" />
                  WhatsApp
                </Button>
              </div>
              <p className="text-xs text-[#6B5D54] text-center mt-3">
                Paiement sécurisé Stripe (Apple Pay / Google Pay) ou commande directe sur WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SHIPPING OPTIONS HIGHLIGHT ============ */}
      <section className="py-16 md:py-20 bg-[#F5EFE6]/50">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Recevez votre commande</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">3 façons de l&apos;obtenir.</h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {SHIPPING_OPTIONS.map((s) => {
              const Icon = shippingIcons[s.id] || Truck;
              return (
                <div key={s.id} className="rounded-3xl bg-white border border-[#EADBD1] p-6 hover:-translate-y-1 transition-transform duration-300">
                  <span className="h-11 w-11 rounded-full bg-[#F5EFE6] grid place-items-center">
                    <Icon className="h-5 w-5 text-[#C58C85]" strokeWidth={1.5} />
                  </span>
                  <h3 className="font-serif text-xl mt-4 text-[#2C2420]">{s.label}</h3>
                  <p className={`text-sm font-medium mt-1 ${s.price === 0 ? "text-[#25D366]" : "text-[#2C2420]"}`}>{shippingLine(s)}</p>
                  <p className="text-xs text-[#6B5D54] mt-3">{s.description}</p>
                  <p className="text-[11px] uppercase tracking-wider text-[#C58C85] mt-3">{s.eta}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ FEATURES BENTO ============ */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Pourquoi nos clientes adorent</p>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">
              Une fibre pensée pour <span className="italic">vous</span>.
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5">
            <div className="md:col-span-7 rounded-3xl bg-white p-8 md:p-10 border border-[#EADBD1] hover:-translate-y-1 transition-transform duration-300">
              <Droplets className="h-7 w-7 text-[#C58C85]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl mt-4 text-[#2C2420]">Thermo-réglable à l&apos;eau chaude</h3>
              <p className="text-[#6B5D54] mt-3 max-w-md">
                Activez la boucle en quelques secondes. Une trempette dans l&apos;eau bouillante et votre French Curl prend forme,
                définit son ondulation et tient toute la durée de la pose.
              </p>
            </div>
            <div className="md:col-span-5 rounded-3xl overflow-hidden bg-[#2C2420] text-white p-8 md:p-10 hover:-translate-y-1 transition-transform duration-300">
              <Sparkles className="h-7 w-7 text-[#C58C85]" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl mt-4">Fibre douce, brillance naturelle</h3>
              <p className="text-white/70 mt-3">Aucun effet plastique. Une fibre soyeuse qui imite la chute d&apos;un cheveu naturel.</p>
            </div>
            <div className="md:col-span-4 rounded-3xl bg-white p-8 border border-[#EADBD1] hover:-translate-y-1 transition-transform duration-300">
              <Flame className="h-7 w-7 text-[#C58C85]" strokeWidth={1.5} />
              <h3 className="font-serif text-xl mt-4 text-[#2C2420]">Pose rapide</h3>
              <p className="text-[#6B5D54] mt-2 text-sm">2 à 3h pour une pose complète chez votre coiffeuse.</p>
            </div>
            <div className="md:col-span-4 rounded-3xl bg-white p-8 border border-[#EADBD1] hover:-translate-y-1 transition-transform duration-300">
              <Shield className="h-7 w-7 text-[#C58C85]" strokeWidth={1.5} />
              <h3 className="font-serif text-xl mt-4 text-[#2C2420]">Tenue 4 à 8 semaines</h3>
              <p className="text-[#6B5D54] mt-2 text-sm">Avec un entretien doux, gardez vos braids impeccables.</p>
            </div>
            <div className="md:col-span-4 rounded-3xl bg-white p-8 border border-[#EADBD1] hover:-translate-y-1 transition-transform duration-300">
              <Heart className="h-7 w-7 text-[#C58C85]" strokeWidth={1.5} />
              <h3 className="font-serif text-xl mt-4 text-[#2C2420]">Cuir chevelu apaisé</h3>
              <p className="text-[#6B5D54] mt-2 text-sm">Une fibre légère qui ne tire pas, qui ne gratte pas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section id="avis" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Elles en parlent</p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420]">
                <span className="italic">&ldquo;Mes cheveux,</span> en mieux.&rdquo;
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <StarRow />
              <span className="text-sm text-[#6B5D54]">4,9/5 — 1 240 avis vérifiés</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <article key={i} data-testid={`testimonial-${i}`} className="rounded-3xl bg-white border border-[#EADBD1] p-6 hover:-translate-y-1 transition-transform duration-300">
                <StarRow n={t.rating} />
                <p className="text-[#2C2420] mt-4 text-[15px] leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-[#EADBD1]">
                  <p className="font-medium text-sm text-[#2C2420]">{t.name}</p>
                  <p className="text-xs text-[#6B5D54]">{t.city}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-16 rounded-[2rem] overflow-hidden bg-[#2C2420] text-white grid grid-cols-1 md:grid-cols-2">
            <div className="p-10 md:p-14">
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight">
                Prête à essayer la <span className="italic text-[#C58C85]">French Curl</span> ?
              </h3>
              <p className="text-white/70 mt-4 max-w-md">
                Commandez en 30 secondes via WhatsApp. Notre équipe vous confirme la disponibilité, votre mode de livraison et un paiement sécurisé.
              </p>
              <Button onClick={handleWhatsApp} className="mt-7 rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white px-7 h-14" data-testid="banner-whatsapp-btn">
                <WhatsAppIcon className="h-5 w-5 mr-2.5" />
                Commander maintenant
              </Button>
              <p className="text-xs text-white/50 mt-4">Livraison dès {formatEUR(5.9)} · Click & Collect gratuit</p>
            </div>
            <div className="relative min-h-[260px] md:min-h-0">
              <img src={IMG_TESTI} alt="Cliente heureuse" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section id="faq" className="py-20 md:py-28 bg-[#F5EFE6]/50">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85] text-center">Questions fréquentes</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3 text-[#2C2420] text-center">On vous répond.</h2>

          <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border-b border-[#EADBD1]">
                <AccordionTrigger className="text-left text-base md:text-lg font-medium text-[#2C2420] hover:no-underline py-6">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6B5D54] text-[15px] leading-relaxed pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ============ NEWSLETTER ============ */}
      <section id="newsletter" className="py-20 md:py-28 bg-[#2C2420] text-white relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center relative z-10">
          <p className="text-xs uppercase tracking-[0.28em] font-medium text-[#C58C85]">Restez dans la boucle</p>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-3">
            Recevez nos <span className="italic text-[#C58C85]">nouveautés</span> & promos.
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            Inscrivez-vous à la newsletter : nouvelles teintes en avant-première, offres exclusives, conseils d&apos;entretien.
          </p>
          <div className="mt-8 flex justify-center">
            <NewsletterForm source="landing-footer" />
          </div>
          <p className="text-[11px] text-white/40 mt-4">Désinscription en un clic. Vos données sont en sécurité.</p>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="bg-[#2C2420] text-[#FDFBF9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <p className="font-serif text-5xl md:text-7xl leading-none tracking-tighter">French Curl</p>
              <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#C58C85] mt-2">Vibes</p>
              <p className="text-white/70 mt-6 max-w-md">
                La maison de la French Curl premium. Une fibre, 60+ teintes, longueur unique 24&quot;. Commande simple, livraison rapide ou retrait gratuit.
              </p>
              <Button onClick={handleWhatsApp} className="mt-6 rounded-full bg-[#25D366] hover:bg-[#1EAB53] text-white px-6 h-12" data-testid="footer-whatsapp-btn">
                <WhatsAppIcon className="h-4 w-4 mr-2" />
                Discuter avec nous
              </Button>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-4">Navigation</p>
              <ul className="space-y-3 text-white/80 text-sm">
                <li><a href="#produit" className="hover:text-[#C58C85] transition-colors">Le produit</a></li>
                <li><a href="#couleurs" className="hover:text-[#C58C85] transition-colors">Couleurs</a></li>
                <li><a href="#avis" className="hover:text-[#C58C85] transition-colors">Avis</a></li>
                <li><a href="#faq" className="hover:text-[#C58C85] transition-colors">FAQ</a></li>
                <li><Link to="/partenaires" className="hover:text-[#C58C85] transition-colors" data-testid="footer-partners-link">Partenaires</Link></li>
              </ul>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-4">Légal</p>
              <ul className="space-y-3 text-white/80 text-sm">
                <li><Link to="/legal/mentions-legales" className="hover:text-[#C58C85] transition-colors" data-testid="footer-mentions-link">Mentions légales</Link></li>
                <li><Link to="/legal/confidentialite" className="hover:text-[#C58C85] transition-colors" data-testid="footer-privacy-link">Confidentialité</Link></li>
                <li><Link to="/legal/cgv" className="hover:text-[#C58C85] transition-colors" data-testid="footer-cgv-link">CGV & retours</Link></li>
                <li><Link to="/legal/entretien" className="hover:text-[#C58C85] transition-colors" data-testid="footer-care-link">Guide d&apos;entretien</Link></li>
                <li><Link to="/legal/faq-detaillee" className="hover:text-[#C58C85] transition-colors" data-testid="footer-faq-link">FAQ détaillée</Link></li>
              </ul>
            </div>
            <div className="md:col-span-3">
              <p className="text-xs uppercase tracking-[0.25em] text-white/50 mb-4">Contact</p>
              <ul className="space-y-3 text-white/80 text-sm">
                <li className="flex items-center gap-2"><Mail className="h-4 w-4" /> <a href={`mailto:${BRAND.email}`} className="hover:text-[#C58C85]">{BRAND.email}</a></li>
                <li className="flex items-center gap-2"><Instagram className="h-4 w-4" /> {BRAND.instagram}</li>
                <li className="flex items-center gap-2"><WhatsAppIcon className="h-4 w-4" color="#FDFBF9" /> WhatsApp 7j/7</li>
              </ul>
            </div>
          </div>
          <Separator className="my-10 bg-white/10" />
          <div className="flex flex-col md:flex-row justify-between gap-3 text-xs text-white/50">
            <p>© {new Date().getFullYear()} {BRAND.name}. Tous droits réservés.</p>
            <p>France 5,90€ · Europe 9,90€ · Click & Collect gratuit</p>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp button */}
      <button
        onClick={handleWhatsApp}
        aria-label="Commander sur WhatsApp"
        data-testid="floating-whatsapp-btn"
        className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1EAB53] grid place-items-center shadow-xl shadow-[#25D366]/40 animate-soft-pulse transition-transform active:scale-95"
      >
        <WhatsAppIcon className="h-6 w-6" color="#fff" />
      </button>

      <WelcomePopup />
    </div>
  );
}
